import { WebAuthnAbortService } from '@simplewebauthn/browser';

/** Capacitor Android WebView often lacks PublicKeyCredential; desktop browsers have it. */
export function browserSupportsWebAuthn(): boolean {
  try {
    return typeof globalThis !== 'undefined' && typeof PublicKeyCredential !== 'undefined';
  } catch {
    return false;
  }
}

/** Only one navigator.credentials.get/create at a time (SimpleWebAuthn aborts the previous). */
let passkeyCeremonyLock: Promise<unknown> | null = null;

/** Abort any in-flight WebAuthn ceremony and clear the module lock (call on unmount / logout). */
export function abortPasskeyCeremony(reason?: string): void {
  const hadLock = !!passkeyCeremonyLock;
  // Calling cancelCeremony() with no active get() can leave Win11/Chrome in a bad state so the
  // next Microsoft Password Manager selection fails once with NotAllowedError.
  if (!hadLock && reason === 'runPasskey-before-get') return;
  WebAuthnAbortService.cancelCeremony();
  passkeyCeremonyLock = null;
}

export function hasPasskeyCeremonyLock(): boolean {
  return passkeyCeremonyLock != null;
}

/**
 * Run a WebAuthn ceremony without overlapping a previous get()/create().
 * A second call cancels the first mid-PIN ("Something went wrong") on purpose so only one OS prompt runs.
 */
export async function runExclusivePasskeyCeremony<T>(run: () => Promise<T>): Promise<T> {
  if (passkeyCeremonyLock) {
    WebAuthnAbortService.cancelCeremony();
    try {
      await passkeyCeremonyLock;
    } catch {
      /* previous ceremony cancelled */
    }
  }

  const ceremony = Promise.resolve().then(run);
  passkeyCeremonyLock = ceremony.finally(() => {
    if (passkeyCeremonyLock === ceremony) passkeyCeremonyLock = null;
  });
  return ceremony;
}
